import useUiTranslation from "../utils/useUiTranslation";
import React, { useMemo, useState } from "react";
import { ArrowRight, CalendarRange, PackageCheck, TrendingDown, TrendingUp } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  calculateSafetyStock as calculateSafetyStockApi,
  fetchPlanningDefaults,
  fetchDemandForecast,
  runReplenishmentPlanner,
  savePlanningDefaults as savePlanningDefaultsApi,
  saveReplenishmentPolicy,
  runMrp,
} from "../api/planning";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import formatNumber from "../utils/formatNumber";
import ForecastingDemandModuleCard from "../components/planning/ForecastingDemandModuleCard";

const defaultForecastForm = {
  item_name: "",
  method: "moving_average",
  horizon_months: 6,
  window_size: 3,
  sop_adjustments: [],
};

const defaultSafetyForm = {
  item_name: "",
  service_level: 0.95,
  lead_time_days: 14,
  review_period_days: 7,
  on_hand: 0,
  on_order: 0,
};

const defaultMrpItem = {
  item_name: "",
  on_hand: 0,
  on_order: 0,
  safety_stock: 0,
  lead_time_days: 14,
  lot_size: 0,
  horizon_days: 84,
  bucket_days: 7,
};

const SAFETY_SCHEMA_NOTE =
  "Warehouse stock levels track quantity only. Min/max targets, safety stock, and reorder thresholds are advisory outputs and are not stored in the inventory schema.";

const defaultReplenishmentPolicy = {
  warehouse_id: "",
  stock_item_id: "",
  reorder_point: 0,
  safety_stock: 0,
  lead_time_days: 14,
  review_period_days: 7,
  lot_size: 0,
  is_active: true,
};

export const summarizeForecast = (forecast = []) => {
  const quantities = forecast.map((entry) => Number(entry.forecast_qty) || 0);
  const total = quantities.reduce((sum, quantity) => sum + quantity, 0);
  const average = quantities.length ? total / quantities.length : 0;
  const peakIndex = quantities.indexOf(Math.max(...quantities));
  const first = quantities[0] || 0;
  const last = quantities[quantities.length - 1] || 0;
  const change = first ? ((last - first) / first) * 100 : 0;

  return {
    total,
    average,
    change,
    peak: peakIndex >= 0 ? forecast[peakIndex] : null,
  };
};

const PlanningWorkbench = () => {
  const uiText = useUiTranslation();
  const { user } = useCurrentUser();
  const canManage = hasPermission(user,'planning.manage');
  const canManageInstitute = hasPermission(user,'planning.cross-department') && hasPermission(user,'planning.cross-warehouse');
  const canReplenish = hasPermission(user,'replenishment.manage');
  const [forecastForm, setForecastForm] = useState(defaultForecastForm);
  const [sopRows, setSopRows] = useState([]);
  const [seasonalityRows, setSeasonalityRows] = useState([]);
  const [departmentRows, setDepartmentRows] = useState([]);
  const [oncologyRows, setOncologyRows] = useState([]);
  const [stockOutRows, setStockOutRows] = useState([]);
  const [forecastStockLimits, setForecastStockLimits] = useState({ min_monthly_stock: "", max_monthly_stock: "" });
  const [forecastResult, setForecastResult] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState("");

  const [safetyForm, setSafetyForm] = useState(defaultSafetyForm);
  const [safetyResult, setSafetyResult] = useState(null);
  const [safetyLoading, setSafetyLoading] = useState(false);
  const [safetyError, setSafetyError] = useState("");

  const [mrpItem, setMrpItem] = useState(defaultMrpItem);
  const [demandRows, setDemandRows] = useState([{ date: "", quantity: 0 }]);
  const [openOrders, setOpenOrders] = useState([]);
  const [mrpResult, setMrpResult] = useState(null);
  const [mrpLoading, setMrpLoading] = useState(false);
  const [mrpError, setMrpError] = useState("");

  const [policyForm, setPolicyForm] = useState(defaultReplenishmentPolicy);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyResult, setPolicyResult] = useState(null);
  const [policyError, setPolicyError] = useState("");
  const [replenishmentLoading, setReplenishmentLoading] = useState(false);
  const [replenishmentTasks, setReplenishmentTasks] = useState([]);
  const [replenishmentError, setReplenishmentError] = useState("");
  const [defaultsWarehouseId, setDefaultsWarehouseId] = useState("");
  const [defaultsMessage, setDefaultsMessage] = useState("");

  const loadPlanningDefaults = async () => {
    try {
    const warehouseId = defaultsWarehouseId ? Number(defaultsWarehouseId) : undefined;
    const res = await fetchPlanningDefaults(warehouseId);
    const s = res.data.settings || {};
    setForecastForm((f) => ({ ...f, horizon_months: Number(s.forecast_horizon_months || 6), window_size: Number(s.forecast_window_size || 3) }));
    setSafetyForm((f) => ({ ...f, lead_time_days: Number(s.safety_lead_time_days || 14), review_period_days: Number(s.safety_review_period_days || 7) }));
    setMrpItem((f) => ({ ...f, horizon_days: Number(s.mrp_horizon_days || 84), bucket_days: Number(s.mrp_bucket_days || 7), lead_time_days: Number(s.safety_lead_time_days || 14) }));
    setPolicyForm((f) => ({ ...f, lead_time_days: Number(s.safety_lead_time_days || 14), review_period_days: Number(s.safety_review_period_days || 7), warehouse_id: warehouseId || f.warehouse_id }));
    setDefaultsMessage("Defaults loaded.");
    } catch (error) { setDefaultsMessage(error.response?.data?.message || "Unable to load defaults"); }
  };

  const savePlanningDefaults = async () => {
    try {
    await savePlanningDefaultsApi({
      warehouse_id: defaultsWarehouseId ? Number(defaultsWarehouseId) : null,
      forecast_horizon_months: forecastForm.horizon_months,
      forecast_window_size: forecastForm.window_size,
      safety_lead_time_days: safetyForm.lead_time_days,
      safety_review_period_days: safetyForm.review_period_days,
      mrp_horizon_days: mrpItem.horizon_days,
      mrp_bucket_days: mrpItem.bucket_days,
    });
    setDefaultsMessage("Defaults saved.");
    } catch (error) { setDefaultsMessage(error.response?.data?.message || "Unable to save defaults"); }
  };

  const addSopRow = () => {
    setSopRows((rows) => [...rows, { period: "", adjustment: 0 }]);
  };

  const updateSopRow = (index, field, value) => {
    setSopRows((rows) =>
      rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)),
    );
  };

  const removeSopRow = (index) => {
    setSopRows((rows) => rows.filter((_, idx) => idx !== index));
  };

  const addFactorRow = (setter) => setter((rows) => [...rows, { period: "", factor: 1 }]);
  const updateFactorRow = (setter, index, field, value) => {
    setter((rows) => rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };
  const removeFactorRow = (setter, index) => setter((rows) => rows.filter((_, idx) => idx !== index));

  const handleForecastSubmit = async (event) => {
    event.preventDefault();
    setForecastLoading(true);
    setForecastError("");
    setForecastResult(null);

    try {
      const payload = {
        ...forecastForm,
        sop_adjustments: sopRows,
        seasonality: seasonalityRows,
        department_activity: departmentRows,
        oncology_protocols: oncologyRows,
        stock_outs: stockOutRows,
        min_monthly_stock: forecastStockLimits.min_monthly_stock === "" ? null : Number(forecastStockLimits.min_monthly_stock),
        max_monthly_stock: forecastStockLimits.max_monthly_stock === "" ? null : Number(forecastStockLimits.max_monthly_stock),
      };
      const res = await fetchDemandForecast(payload);
      setForecastResult(res.data);
    } catch (err) {
      console.error("Failed to generate forecast", err);
      setForecastError(
        err?.response?.data?.message ||
          "Unable to generate a forecast. Please try again.",
      );
    } finally {
      setForecastLoading(false);
    }
  };

  const handleSafetySubmit = async (event) => {
    event.preventDefault();
    setSafetyLoading(true);
    setSafetyError("");
    setSafetyResult(null);

    try {
      const res = await calculateSafetyStockApi(safetyForm);
      setSafetyResult(res.data);
    } catch (err) {
      console.error("Failed to calculate safety stock", err);
      setSafetyError(
        err?.response?.data?.message ||
          "Unable to calculate safety stock. Please try again.",
      );
    } finally {
      setSafetyLoading(false);
    }
  };

  const handleMrpSubmit = async (event) => {
    event.preventDefault();
    setMrpLoading(true);
    setMrpError("");
    setMrpResult(null);

    try {
      const payload = {
        horizon_days: mrpItem.horizon_days,
        bucket_days: mrpItem.bucket_days,
        items: [
          {
            ...mrpItem,
            forecast: demandRows.filter((row) => row.date && row.quantity),
            open_orders: openOrders.filter(
              (row) => row.due_date && row.quantity,
            ),
          },
        ],
      };
      const res = await runMrp(payload);
      setMrpResult(res.data);
    } catch (err) {
      console.error("Failed to run MRP", err);
      setMrpError(
        err?.response?.data?.message || "Unable to run MRP. Please try again.",
      );
    } finally {
      setMrpLoading(false);
    }
  };

  const handlePolicySubmit = async (event) => {
    event.preventDefault();
    setPolicyLoading(true);
    setPolicyError("");
    setPolicyResult(null);

    try {
      const payload = {
        ...policyForm,
        warehouse_id: Number(policyForm.warehouse_id),
        stock_item_id: Number(policyForm.stock_item_id),
      };
      const res = await saveReplenishmentPolicy(payload);
      setPolicyResult(res.data.policy);
    } catch (err) {
      console.error("Failed to save replenishment policy", err);
      setPolicyError(
        err?.response?.data?.message ||
          "Unable to save the replenishment policy. Please try again.",
      );
    } finally {
      setPolicyLoading(false);
    }
  };

  const handleReplenishmentRun = async () => {
    setReplenishmentLoading(true);
    setReplenishmentError("");
    setReplenishmentTasks([]);

    try {
      const res = await runReplenishmentPlanner({
        warehouse_id: Number(policyForm.warehouse_id),
      });
      setReplenishmentTasks(res.data.tasks || []);
    } catch (err) {
      console.error("Failed to run replenishment planner", err);
      setReplenishmentError(
        err?.response?.data?.message ||
          "Unable to generate replenishment tasks. Please try again.",
      );
    } finally {
      setReplenishmentLoading(false);
    }
  };

  const adoptForecastForMrp = () => {
    if (!forecastResult?.forecast?.length) return;
    setMrpItem((current) => ({ ...current, item_name: forecastForm.item_name }));
    const mapped = forecastResult.forecast.map((entry) => ({
      date: `${entry.month}-01`,
      quantity: entry.forecast_qty,
    }));
    setDemandRows(mapped);
  };

  const addDemandRow = () =>
    setDemandRows((rows) => [...rows, { date: "", quantity: 0 }]);

  const updateDemandRow = (index, field, value) => {
    setDemandRows((rows) =>
      rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)),
    );
  };

  const removeDemandRow = (index) => {
    setDemandRows((rows) => rows.filter((_, idx) => idx !== index));
  };

  const addOpenOrder = () =>
    setOpenOrders((rows) => [...rows, { due_date: "", quantity: 0 }]);

  const updateOpenOrder = (index, field, value) => {
    setOpenOrders((rows) =>
      rows.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)),
    );
  };

  const removeOpenOrder = (index) => {
    setOpenOrders((rows) => rows.filter((_, idx) => idx !== index));
  };

  const forecastTable = useMemo(() => {
    if (!forecastResult?.forecast?.length) return null;
    return (
      <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200" aria-label="Forecast table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                Period
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                Forecast Qty
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {forecastResult.forecast.map((entry) => (
              <tr key={entry.month} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">{entry.month}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatNumber(entry.forecast_qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [forecastResult]);

  const bucketTable = useMemo(() => {
    if (!mrpResult?.plans?.[0]?.buckets?.length) return null;
    const buckets = mrpResult.plans[0].buckets;
    return (
      <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200" aria-label="Bucketed demand table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Bucket</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Start</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Demand</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Receipts</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Projected Available</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {buckets.map((bucket) => (
              <tr key={bucket.index} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">Week {bucket.index}</td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {new Date(bucket.start).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatNumber(bucket.demand)}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatNumber(bucket.receipts)}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatNumber(bucket.projected_available)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [mrpResult]);

  const plannedOrders = useMemo(() => {
    if (!mrpResult?.plans?.[0]?.planned_orders?.length) return null;
    const orders = mrpResult.plans[0].planned_orders;
    return (
      <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200" aria-label="Planned orders table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Need by</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Release</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">{uiText("Quantity")}</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Bucket</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.map((order, index) => (
              <tr key={`${order.need_by}-${index}`} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                  {new Date(order.need_by).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {new Date(order.planned_release).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatNumber(order.quantity)}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{order.bucket}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [mrpResult, uiText]);

  const forecastInsights = useMemo(
    () => summarizeForecast(forecastResult?.forecast),
    [forecastResult],
  );

  const forecastChartData = useMemo(() => {
    const history = (forecastResult?.history || []).map((entry) => ({
      period: entry.bucket,
      actual: Number(entry.quantity) || 0,
      forecast: null,
    }));
    const forecast = (forecastResult?.forecast || []).map((entry) => ({
      period: entry.month,
      actual: null,
      forecast: Number(entry.forecast_qty) || 0,
    }));
    if (history.length && forecast.length) {
      forecast.unshift({ ...history[history.length - 1], forecast: history[history.length - 1].actual });
    }
    return [...history, ...forecast];
  }, [forecastResult]);

  return (
    <>
      <main className="mx-auto w-full max-w-6xl space-y-10 p-6">
        <header className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">Demand planning</p>
          <h1 className="text-3xl font-bold text-gray-900">Forecasting, safety stock, and MRP</h1>
          <p className="max-w-3xl text-sm text-gray-600">
            Use the new planning services to build statistical forecasts, compute safety stock policies, and net demand into dated planned orders.
          </p>
        </header>

        <ForecastingDemandModuleCard />
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-end gap-3">
            <div>
              <label htmlFor="planning-defaults-warehouse" className="block text-sm font-medium text-gray-700">Warehouse override (optional)</label>
              <input id="planning-defaults-warehouse" className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm" value={defaultsWarehouseId} onChange={(e) => setDefaultsWarehouseId(e.target.value)} placeholder="Institute defaults when blank" />
            </div>
            <button type="button" onClick={loadPlanningDefaults} className="rounded bg-slate-700 px-3 py-2 text-sm font-medium text-white">Load defaults</button>
            <button type="button" onClick={savePlanningDefaults} disabled={!canManage || (!defaultsWarehouseId && !canManageInstitute)} className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white">Save current as defaults</button>
          </div>
          {defaultsMessage && <p className="mt-2 text-sm text-green-700">{defaultsMessage}</p>}
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Demand forecast</h2>
                <p className="text-sm text-gray-600">Generate a monthly forecast using moving-average or linear trend.</p>
              </div>
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Forecast</span>
            </div>

            <form className="mt-4 space-y-4" onSubmit={handleForecastSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="forecast-item">{uiText("Item name")}</label>
                <input
                  id="forecast-item"
                  type="text"
                  value={forecastForm.item_name}
                  onChange={(e) =>
                    setForecastForm((form) => ({ ...form, item_name: e.target.value }))
                  }
                  required
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder={uiText("e.g. Surgical gloves")}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="forecast-method">
                    Method
                  </label>
                  <select
                    id="forecast-method"
                    value={forecastForm.method}
                    onChange={(e) =>
                      setForecastForm((form) => ({ ...form, method: e.target.value }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="moving_average">Moving average</option>
                    <option value="linear_trend">Linear trend</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="forecast-horizon">
                    Horizon (months)
                  </label>
                  <input
                    id="forecast-horizon"
                    type="number"
                    min="1"
                    max="18"
                    value={forecastForm.horizon_months}
                    onChange={(e) =>
                      setForecastForm((form) => ({
                        ...form,
                        horizon_months: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="forecast-window">
                    Window size
                  </label>
                  <input
                    id="forecast-window"
                    type="number"
                    min="1"
                    max="12"
                    value={forecastForm.window_size}
                    onChange={(e) =>
                      setForecastForm((form) => ({
                        ...form,
                        window_size: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={forecastForm.method !== "moving_average"}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">S&OP adjustments</h3>
                    <p className="text-xs text-gray-500">Optional overrides by period (YYYY-MM).</p>
                  </div>
                  <button
                    type="button"
                    onClick={addSopRow}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Add adjustment
                  </button>
                </div>
                {sopRows.length === 0 && (
                  <p className="text-xs text-gray-500">No overrides added.</p>
                )}
                <div className="space-y-2">
                  {sopRows.map((row, index) => (
                    <div key={`sop-${index}`} className="grid gap-3 sm:grid-cols-5 sm:items-end">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600" htmlFor={`period-${index}`}>
                          Period (YYYY-MM)
                        </label>
                        <input
                          id={`period-${index}`}
                          type="text"
                          value={row.period}
                          onChange={(e) => updateSopRow(index, "period", e.target.value)}
                          placeholder="2024-11"
                          className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600" htmlFor={`adjustment-${index}`}>
                          Adjustment
                        </label>
                        <input
                          id={`adjustment-${index}`}
                          type="number"
                          value={row.adjustment}
                          onChange={(e) =>
                            updateSopRow(index, "adjustment", Number(e.target.value))
                          }
                          className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeSopRow(index)}
                          className="text-sm font-semibold text-red-600 hover:text-red-700"
                        >{uiText("Remove")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600">Min monthly stock (optional)</label>
                  <input type="number" value={forecastStockLimits.min_monthly_stock} onChange={(e) => setForecastStockLimits((c) => ({ ...c, min_monthly_stock: e.target.value }))} className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Max monthly stock (optional)</label>
                  <input type="number" value={forecastStockLimits.max_monthly_stock} onChange={(e) => setForecastStockLimits((c) => ({ ...c, max_monthly_stock: e.target.value }))} className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>

              {[{ title: "Seasonality factors", rows: seasonalityRows, setter: setSeasonalityRows }, { title: "Department activity factors", rows: departmentRows, setter: setDepartmentRows }, { title: "Oncology protocol factors", rows: oncologyRows, setter: setOncologyRows }, { title: "Stock-out correction factors", rows: stockOutRows, setter: setStockOutRows }].map((group) => (
                <div key={group.title} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">{group.title}</h3>
                    <button type="button" onClick={() => addFactorRow(group.setter)} className="text-sm font-semibold text-blue-600 hover:text-blue-700">Add factor</button>
                  </div>
                  {group.rows.length === 0 && <p className="text-xs text-gray-500">No monthly factors added.</p>}
                  {group.rows.map((row, index) => (
                    <div key={`${group.title}-${index}`} className="grid gap-3 sm:grid-cols-5 sm:items-end">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600">Period (YYYY-MM)</label>
                        <input type="text" value={row.period} onChange={(e) => updateFactorRow(group.setter, index, "period", e.target.value)} className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600">Factor</label>
                        <input type="number" step="0.01" min="0" value={row.factor} onChange={(e) => updateFactorRow(group.setter, index, "factor", Number(e.target.value))} className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
                      </div>
                      <div className="flex justify-end">
                        <button type="button" onClick={() => removeFactorRow(group.setter, index)} className="text-sm font-semibold text-red-600 hover:text-red-700">{uiText("Remove")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <button
                type="submit"
                className="inline-flex items-center justify-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                disabled={forecastLoading}
              >
                {forecastLoading ? "Generating forecast…" : "Run forecast"}
              </button>

              {forecastError && (
                <p className="text-sm text-red-600" role="alert">
                  {forecastError}
                </p>
              )}
            </form>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Safety stock policy</h2>
                <p className="text-sm text-gray-600">Calculate reorder point and buffer based on daily volatility.</p>
                <p className="mt-1 text-xs text-gray-500">{SAFETY_SCHEMA_NOTE}</p>
              </div>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Safety</span>
            </div>

            <form className="space-y-4" onSubmit={handleSafetySubmit}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="safety-item">{uiText("Item name")}</label>
                  <input
                    id="safety-item"
                    type="text"
                    value={safetyForm.item_name}
                    onChange={(e) =>
                      setSafetyForm((form) => ({ ...form, item_name: e.target.value }))
                    }
                    required
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Same item as forecast"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="service-level">
                    Service level (0-1)
                  </label>
                  <input
                    id="service-level"
                    type="number"
                    step="0.01"
                    min="0.5"
                    max="0.999"
                    value={safetyForm.service_level}
                    onChange={(e) =>
                      setSafetyForm((form) => ({
                        ...form,
                        service_level: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="lead-time">{uiText("Lead time (days)")}</label>
                  <input
                    id="lead-time"
                    type="number"
                    min="1"
                    value={safetyForm.lead_time_days}
                    onChange={(e) =>
                      setSafetyForm((form) => ({
                        ...form,
                        lead_time_days: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="review-period">
                    Review period (days)
                  </label>
                  <input
                    id="review-period"
                    type="number"
                    min="1"
                    value={safetyForm.review_period_days}
                    onChange={(e) =>
                      setSafetyForm((form) => ({
                        ...form,
                        review_period_days: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="on-hand">
                    On hand
                  </label>
                  <input
                    id="on-hand"
                    type="number"
                    min="0"
                    value={safetyForm.on_hand}
                    onChange={(e) =>
                      setSafetyForm((form) => ({
                        ...form,
                        on_hand: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="on-order">
                    On order
                  </label>
                  <input
                    id="on-order"
                    type="number"
                    min="0"
                    value={safetyForm.on_order}
                    onChange={(e) =>
                      setSafetyForm((form) => ({
                        ...form,
                        on_order: Number(e.target.value),
                      }))
                    }
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="inline-flex items-center justify-center rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                disabled={safetyLoading}
              >
                {safetyLoading ? "Calculating…" : "Calculate"}
              </button>

              {safetyError && (
                <p className="text-sm text-red-600" role="alert">
                  {safetyError}
                </p>
              )}

              {safetyResult && (
                <div className="rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                  <p className="font-semibold">Reorder point: {formatNumber(safetyResult.reorder_point)}</p>
                  <p>Safety stock: {formatNumber(safetyResult.safety_stock)}</p>
                  <p>Recommended order (gap): {formatNumber(safetyResult.reorder_recommendation)}</p>
                  <p className="mt-2 text-xs text-emerald-700">
                    {safetyResult.advisory_note || SAFETY_SCHEMA_NOTE}
                  </p>
                  <p className="mt-2 text-xs text-emerald-700">
                    Avg daily demand {formatNumber(safetyResult.average_daily_demand)} · Std dev {formatNumber(safetyResult.demand_std_dev)} · Z {safetyResult.z_value}
                  </p>
                </div>
              )}
            </form>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Replenishment policies</h2>
              <p className="text-sm text-gray-600">
                Define reorder thresholds per warehouse and generate replenishment tasks when stock falls below policy.
              </p>
            </div>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              Replenishment
            </span>
          </div>

          <form className="space-y-4" onSubmit={handlePolicySubmit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-warehouse">
                  Warehouse ID
                </label>
                <input
                  id="policy-warehouse"
                  type="number"
                  min="1"
                  value={policyForm.warehouse_id}
                  onChange={(e) =>
                    setPolicyForm((form) => ({ ...form, warehouse_id: e.target.value }))
                  }
                  required
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-item">
                  Stock item ID
                </label>
                <input
                  id="policy-item"
                  type="number"
                  min="1"
                  value={policyForm.stock_item_id}
                  onChange={(e) =>
                    setPolicyForm((form) => ({ ...form, stock_item_id: e.target.value }))
                  }
                  required
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-reorder">
                  Reorder point
                </label>
                <input
                  id="policy-reorder"
                  type="number"
                  min="0"
                  value={policyForm.reorder_point}
                  onChange={(e) =>
                    setPolicyForm((form) => ({
                      ...form,
                      reorder_point: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-safety">
                  Safety stock
                </label>
                <input
                  id="policy-safety"
                  type="number"
                  min="0"
                  value={policyForm.safety_stock}
                  onChange={(e) =>
                    setPolicyForm((form) => ({
                      ...form,
                      safety_stock: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-lot">
                  Lot size
                </label>
                <input
                  id="policy-lot"
                  type="number"
                  min="0"
                  value={policyForm.lot_size}
                  onChange={(e) =>
                    setPolicyForm((form) => ({
                      ...form,
                      lot_size: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-lead">{uiText("Lead time (days)")}</label>
                <input
                  id="policy-lead"
                  type="number"
                  min="1"
                  value={policyForm.lead_time_days}
                  onChange={(e) =>
                    setPolicyForm((form) => ({
                      ...form,
                      lead_time_days: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="policy-review">
                  Review period (days)
                </label>
                <input
                  id="policy-review"
                  type="number"
                  min="1"
                  value={policyForm.review_period_days}
                  onChange={(e) =>
                    setPolicyForm((form) => ({
                      ...form,
                      review_period_days: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={policyForm.is_active}
                    onChange={(e) =>
                      setPolicyForm((form) => ({ ...form, is_active: e.target.checked }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  Active policy
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                disabled={policyLoading || !canReplenish}
              >
                {policyLoading ? uiText("Saving…") : "Save policy"}
              </button>
              <button
                type="button"
                onClick={handleReplenishmentRun}
                className="inline-flex items-center justify-center rounded border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 shadow-sm hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                disabled={!canReplenish || replenishmentLoading || !policyForm.warehouse_id}
              >
                {replenishmentLoading ? "Generating…" : "Run replenishment"}
              </button>
            </div>

            {policyError && (
              <p className="text-sm text-red-600" role="alert">
                {policyError}
              </p>
            )}

            {policyResult && (
              <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-semibold">
                  Policy saved for warehouse {policyResult.warehouse_id} · item {policyResult.stock_item_id}
                </p>
                <p>
                  Reorder point {formatNumber(policyResult.reorder_point)} · Safety stock {formatNumber(policyResult.safety_stock)} · Lead time{" "}
                  {policyResult.lead_time_days} days
                </p>
              </div>
            )}
          </form>

          {replenishmentError && (
            <p className="text-sm text-red-600" role="alert">
              {replenishmentError}
            </p>
          )}

          {replenishmentTasks.length > 0 && (
            <div className="overflow-x-auto rounded border border-amber-100">
              <table className="min-w-full divide-y divide-amber-100 text-sm">
                <thead className="bg-amber-50 text-amber-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">{uiText("Item")}</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">Current qty</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">Reorder point</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">Suggested qty</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide">{uiText("Due date")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {replenishmentTasks.map((task) => (
                    <tr key={task.id}>
                      <td className="px-4 py-2 text-sm text-amber-900">{task.stock_item_id}</td>
                      <td className="px-4 py-2 text-sm text-amber-900">
                        {formatNumber(task.current_quantity)}
                      </td>
                      <td className="px-4 py-2 text-sm text-amber-900">
                        {formatNumber(task.reorder_point)}
                      </td>
                      <td className="px-4 py-2 text-sm text-amber-900">
                        {formatNumber(task.suggested_quantity)}
                      </td>
                      <td className="px-4 py-2 text-sm text-amber-900">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">MRP / DRP netting</h2>
              <p className="text-sm text-gray-600">Bucket your demand, apply inventory, and generate planned orders.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">Netting</span>
              {forecastResult?.forecast?.length > 0 && (
                <button
                  type="button"
                  onClick={adoptForecastForMrp}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Use forecast above
                </button>
              )}
            </div>
          </div>

          <form className="mt-4 space-y-6" onSubmit={handleMrpSubmit}>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-item">{uiText("Item name")}</label>
                <input
                  id="mrp-item"
                  type="text"
                  value={mrpItem.item_name}
                  onChange={(e) => setMrpItem((item) => ({ ...item, item_name: e.target.value }))}
                  required
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-horizon">
                  Horizon (days)
                </label>
                <input
                  id="mrp-horizon"
                  type="number"
                  min="7"
                  value={mrpItem.horizon_days}
                  onChange={(e) =>
                    setMrpItem((item) => ({
                      ...item,
                      horizon_days: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-bucket">
                  Bucket size (days)
                </label>
                <input
                  id="mrp-bucket"
                  type="number"
                  min="1"
                  value={mrpItem.bucket_days}
                  onChange={(e) =>
                    setMrpItem((item) => ({
                      ...item,
                      bucket_days: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-on-hand">
                  On hand
                </label>
                <input
                  id="mrp-on-hand"
                  type="number"
                  min="0"
                  value={mrpItem.on_hand}
                  onChange={(e) =>
                    setMrpItem((item) => ({ ...item, on_hand: Number(e.target.value) }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-on-order">
                  On order
                </label>
                <input
                  id="mrp-on-order"
                  type="number"
                  min="0"
                  value={mrpItem.on_order}
                  onChange={(e) =>
                    setMrpItem((item) => ({ ...item, on_order: Number(e.target.value) }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-safety">
                  Safety stock
                </label>
                <input
                  id="mrp-safety"
                  type="number"
                  min="0"
                  value={mrpItem.safety_stock}
                  onChange={(e) =>
                    setMrpItem((item) => ({
                      ...item,
                      safety_stock: Number(e.target.value),
                    }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="mrp-lot">
                  Lot size (0 for any)
                </label>
                <input
                  id="mrp-lot"
                  type="number"
                  min="0"
                  value={mrpItem.lot_size}
                  onChange={(e) =>
                    setMrpItem((item) => ({ ...item, lot_size: Number(e.target.value) }))
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Forecasted demand</h3>
                <button
                  type="button"
                  onClick={addDemandRow}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Add row
                </button>
              </div>
              <div className="space-y-2">
                {demandRows.map((row, index) => (
                  <div key={`demand-${index}`} className="grid gap-3 sm:grid-cols-5 sm:items-end">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`demand-date-${index}`}>
                        Demand date
                      </label>
                      <input
                        id={`demand-date-${index}`}
                        type="date"
                        value={row.date}
                        onChange={(e) => updateDemandRow(index, "date", e.target.value)}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`demand-qty-${index}`}>{uiText("Quantity")}</label>
                      <input
                        id={`demand-qty-${index}`}
                        type="number"
                        min="0"
                        value={row.quantity}
                        onChange={(e) =>
                          updateDemandRow(index, "quantity", Number(e.target.value))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeDemandRow(index)}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                      >{uiText("Remove")}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Open orders (scheduled receipts)</h3>
                <button
                  type="button"
                  onClick={addOpenOrder}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Add receipt
                </button>
              </div>
              {openOrders.length === 0 && (
                <p className="text-xs text-gray-500">No scheduled receipts captured.</p>
              )}
              <div className="space-y-2">
                {openOrders.map((row, index) => (
                  <div key={`receipt-${index}`} className="grid gap-3 sm:grid-cols-5 sm:items-end">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`receipt-date-${index}`}>{uiText("Due date")}</label>
                      <input
                        id={`receipt-date-${index}`}
                        type="date"
                        value={row.due_date}
                        onChange={(e) => updateOpenOrder(index, "due_date", e.target.value)}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600" htmlFor={`receipt-qty-${index}`}>{uiText("Quantity")}</label>
                      <input
                        id={`receipt-qty-${index}`}
                        type="number"
                        min="0"
                        value={row.quantity}
                        onChange={(e) =>
                          updateOpenOrder(index, "quantity", Number(e.target.value))
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeOpenOrder(index)}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                      >{uiText("Remove")}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="inline-flex items-center justify-center rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              disabled={mrpLoading}
            >
              {mrpLoading ? "Running MRP…" : "Run MRP"}
            </button>

            {mrpError && (
              <p className="text-sm text-red-600" role="alert">
                {mrpError}
              </p>
            )}
          </form>

          {mrpResult && (
            <div className="mt-6 space-y-4">
              <div className="rounded border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
                <p className="font-semibold">Planned orders: {mrpResult.summary?.total_planned_orders ?? 0}</p>
                <p>Horizon {mrpResult.horizon_days} days · Bucket size {mrpResult.bucket_days} days</p>
              </div>
              {plannedOrders}
              {bucketTable}
            </div>
          )}
        </section>

        {forecastResult && (
          <section className="overflow-hidden rounded-xl border border-blue-100 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-blue-100 bg-gradient-to-r from-slate-950 to-blue-950 p-6 text-white sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-200">
                  Decision-ready forecast
                </p>
                <h2 className="mt-1 text-xl font-semibold">
                  {forecastResult.item_name}
                </h2>
                <p className="mt-1 text-sm text-slate-300">
                  History and projected monthly demand for{" "}
                  {forecastResult.item_name}.
                </p>
                {forecastResult.history_source && (
                  <p className="mt-2 text-xs text-blue-200">
                    History source:{" "}
                    {forecastResult.history_source === "monthly_dispensing"
                      ? "Monthly dispensing feeds"
                      : "Request demand logs"}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={adoptForecastForMrp}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-blue-950 shadow-sm transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-950"
              >
                Build supply plan{" "}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div
                className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
                aria-label="Forecast summary"
              >
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between text-slate-500">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      Horizon demand
                    </span>
                    <CalendarRange className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-950">
                    {formatNumber(forecastInsights.total)}
                  </p>
                  <p className="text-xs text-slate-500">
                    units across {forecastResult.forecast.length} months
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between text-slate-500">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      Monthly run rate
                    </span>
                    <PackageCheck className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-950">
                    {formatNumber(forecastInsights.average)}
                  </p>
                  <p className="text-xs text-slate-500">
                    average forecast demand
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between text-slate-500">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      Peak month
                    </span>
                    <TrendingUp className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-950">
                    {formatNumber(forecastInsights.peak?.forecast_qty)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {forecastInsights.peak?.month || "No projection"}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between text-slate-500">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      Horizon trend
                    </span>
                    {forecastInsights.change >= 0 ? (
                      <TrendingUp className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <TrendingDown className="h-4 w-4" aria-hidden="true" />
                    )}
                  </div>
                  <p
                    className={`mt-2 text-2xl font-bold ${forecastInsights.change > 0 ? "text-amber-700" : "text-emerald-700"}`}
                  >
                    {forecastInsights.change > 0 ? "+" : ""}
                    {forecastInsights.change.toFixed(1)}%
                  </p>
                  <p className="text-xs text-slate-500">
                    first to final forecast month
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Demand trajectory
                    </h3>
                    <p className="text-xs text-slate-500">
                      Actual history transitions into the approved planning
                      scenario.
                    </p>
                  </div>
                  <div className="flex gap-3 text-xs text-slate-600">
                    <span>
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-slate-400" />
                      Actual
                    </span>
                    <span>
                      <span className="mr-1 inline-block h-2 w-2 rounded-full bg-blue-600" />
                      Forecast
                    </span>
                  </div>
                </div>
                <div
                  className="h-72 w-full"
                  aria-label="Historical and forecast demand chart"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={forecastChartData}
                      margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e2e8f0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip
                        formatter={(value) => formatNumber(value)}
                        contentStyle={{
                          borderRadius: 8,
                          borderColor: "#cbd5e1",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        name="Actual"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="forecast"
                        name="Forecast"
                        stroke="#2563eb"
                        strokeWidth={3}
                        dot={{ r: 3 }}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {forecastTable}
              {forecastResult.history?.length > 0 && (
                <details className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  <summary className="cursor-pointer font-semibold text-gray-800">
                    View history used
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-gray-600">
                    {forecastResult.history.map((entry) => (
                      <li key={entry.bucket}>
                        {entry.bucket}: {formatNumber(entry.quantity)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </section>
        )}
      </main>
    </>
  );
};

export default PlanningWorkbench;